import React from 'react';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { SavedPaymentMethodResponse } from '@/client';
import {
	FaCcAmex,
	FaCcDinersClub,
	FaCcDiscover,
	FaCcJcb,
	FaCcMastercard,
	FaCcVisa,
	FaCreditCard,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';

interface SavedCardInfoProps {
	card: SavedPaymentMethodResponse;
}

const MASKED_CARD_DIGITS = '\u2022\u2022\u2022\u2022';

const CARD_BRAND_META: Record<string, { label: string; icon: IconType }> = {
	amex: { label: 'American Express', icon: FaCcAmex },
	diners: { label: 'Diners Club', icon: FaCcDinersClub },
	discover: { label: 'Discover', icon: FaCcDiscover },
	jcb: { label: 'JCB', icon: FaCcJcb },
	mastercard: { label: 'Mastercard', icon: FaCcMastercard },
	visa: { label: 'Visa', icon: FaCcVisa },
};

const formatCardBrand = (brand?: string | null) => {
	if (!brand) {
		return { label: 'Card', icon: FaCreditCard };
	}

	const normalizedBrand = brand.toLowerCase();
	return (
		CARD_BRAND_META[normalizedBrand] ?? {
			label: brand.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
			icon: FaCreditCard,
		}
	);
};

/**
 * Displays masked saved-card metadata so users can verify which card is being charged.
 */
const SavedCardInfo: React.FC<SavedCardInfoProps> = ({ card }) => {
	const brand = formatCardBrand(card.brand);
	const last4 = card.last4 ?? MASKED_CARD_DIGITS;
	const expMonth = card.exp_month ? String(card.exp_month).padStart(2, '0') : '--';
	const expYear = card.exp_year ?? '----';

	return (
		<VStack align="start" gap={1} p={4} borderRadius="xl" border="1px solid" borderColor="border.default" w="full">
			<HStack gap={2}>
				<Icon as={brand.icon} boxSize={6} color="brand.fg" />
				<Text fontWeight="semibold" color="brand.fg">
					{brand.label}
				</Text>
			</HStack>
			<HStack gap={2} color="fg.muted">
				<Text>{MASKED_CARD_DIGITS}</Text>
				<Text>{MASKED_CARD_DIGITS}</Text>
				<Text>{MASKED_CARD_DIGITS}</Text>
				<Text>{last4}</Text>
			</HStack>
			<Text textStyle="body.sm" color="fg.muted">
				Expires {expMonth}/{expYear}
			</Text>
		</VStack>
	);
};

export default SavedCardInfo;
